import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

const SOCIAL_KEYS = ["facebook", "instagram", "twitter", "linkedin", "youtube", "tiktok", "pinterest"];

function Field({ label, help, children, testid }) {
  return (
    <div data-testid={testid ? `settings-field-${testid}` : undefined}>
      <label className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#555]">{label}</label>
      {children}
      {help && <p className="mt-1 text-[10px] text-[#888]">{help}</p>}
    </div>
  );
}

export default function AdminBusinessSettings() {
  const { refresh: refreshSettings } = useSettings();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/admin").then(r => {
      const d = r.data;
      setData({
        ...d,
        phone_numbers_text: (d.phone_numbers || []).join("\n"),
        contact_emails_text: (d.contact_emails || []).join("\n"),
        social_links: { ...SOCIAL_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {}), ...(d.social_links || {}) },
      });
    }).catch(() => toast.error("Failed to load settings"));
  }, []);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const setSocial = (k, v) => setData(prev => ({ ...prev, social_links: { ...prev.social_links, [k]: v } }));

  const payload = useMemo(() => {
    if (!data) return null;
    const social_links = Object.fromEntries(Object.entries(data.social_links || {}).filter(([, v]) => v && v.trim()));
    return {
      business_name: data.business_name || "",
      tagline: data.tagline || "",
      logo_url: data.logo_url || "",
      favicon_url: data.favicon_url || "",
      phone_numbers: (data.phone_numbers_text || "").split("\n").map(s => s.trim()).filter(Boolean),
      contact_emails: (data.contact_emails_text || "").split("\n").map(s => s.trim()).filter(Boolean),
      support_email: data.support_email || "",
      order_notification_email: data.order_notification_email || "",
      physical_address: data.physical_address || "",
      business_hours: data.business_hours || "",
      google_maps_embed_url: data.google_maps_embed_url || "",
      social_links,
      footer_text: data.footer_text || "",
      footer_tagline: data.footer_tagline || "",
      copyright_text: data.copyright_text || "",
      contact_page_text: data.contact_page_text || "",
      seo_title: data.seo_title || "",
      seo_description: data.seo_description || "",
      google_analytics_id: data.google_analytics_id || "",
      meta_pixel_id: data.meta_pixel_id || "",
      google_search_console_verification: data.google_search_console_verification || "",
    };
  }, [data]);

  const save = async () => {
    if (!payload) return;
    setSaving(true);
    try {
      await api.put("/settings", payload);
      await refreshSettings();
      toast.success("Business settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-sm text-[#555]">Loading settings…</p>;

  const inputCls = "mt-1 w-full border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]";

  return (
    <div className="space-y-12" data-testid="admin-settings-panel">
      <div>
        <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Business Settings</p>
        <h2 className="font-serif text-4xl mt-2">Business Information</h2>
        <p className="text-sm text-[#555] mt-3 max-w-2xl">All fields below propagate automatically across the storefront, emails, and SEO metadata. Leave a field blank to hide it site-wide.</p>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Business Name" testid="business-name"><input value={data.business_name || ""} onChange={e => set("business_name", e.target.value)} className={inputCls} data-testid="settings-input-business-name" /></Field>
        <Field label="Header Tagline" help="Shown next to the logo — e.g. 'family tradition'"><input value={data.tagline || ""} onChange={e => set("tagline", e.target.value)} className={inputCls} /></Field>
        <Field label="Logo URL" help="Upload to any image host and paste the URL"><input value={data.logo_url || ""} onChange={e => set("logo_url", e.target.value)} className={inputCls} placeholder="https://..." /></Field>
        <Field label="Favicon URL" help="Recommended 32x32 or 64x64 .ico/.png"><input value={data.favicon_url || ""} onChange={e => set("favicon_url", e.target.value)} className={inputCls} placeholder="https://..." /></Field>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">Contact</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Phone Numbers" help="One per line"><textarea rows={3} value={data.phone_numbers_text || ""} onChange={e => set("phone_numbers_text", e.target.value)} className={inputCls} data-testid="settings-input-phones" placeholder="+1 555 010 1970" /></Field>
          <Field label="Public Contact Emails" help="One per line"><textarea rows={3} value={data.contact_emails_text || ""} onChange={e => set("contact_emails_text", e.target.value)} className={inputCls} data-testid="settings-input-emails" placeholder="hello@yourdomain.com" /></Field>
          <Field label="Default Customer Support Email"><input value={data.support_email || ""} onChange={e => set("support_email", e.target.value)} className={inputCls} placeholder="support@yourdomain.com" /></Field>
          <Field label="Default Order Notification Email"><input value={data.order_notification_email || ""} onChange={e => set("order_notification_email", e.target.value)} className={inputCls} placeholder="orders@yourdomain.com" /></Field>
          <Field label="Physical Address"><textarea rows={3} value={data.physical_address || ""} onChange={e => set("physical_address", e.target.value)} className={inputCls} /></Field>
          <Field label="Business Hours" help="Freeform — e.g. Mon–Fri 9am–6pm ET"><textarea rows={3} value={data.business_hours || ""} onChange={e => set("business_hours", e.target.value)} className={inputCls} /></Field>
          <Field label="Google Maps Embed URL" help="Paste the src URL from Google Maps → Share → Embed"><input value={data.google_maps_embed_url || ""} onChange={e => set("google_maps_embed_url", e.target.value)} className={inputCls} placeholder="https://www.google.com/maps/embed?..." /></Field>
          <Field label="Contact Page Intro Text"><textarea rows={3} value={data.contact_page_text || ""} onChange={e => set("contact_page_text", e.target.value)} className={inputCls} placeholder="Short paragraph shown on the Contact page" /></Field>
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">Social Media</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOCIAL_KEYS.map(k => (
            <Field key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
              <input value={data.social_links[k] || ""} onChange={e => setSocial(k, e.target.value)} className={inputCls} placeholder="https://..." data-testid={`settings-social-${k}`} />
            </Field>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">Footer & Copyright</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Footer Body Text"><textarea rows={3} value={data.footer_text || ""} onChange={e => set("footer_text", e.target.value)} className={inputCls} placeholder="Short brand statement shown in the footer" /></Field>
          <Field label="Footer Tagline"><input value={data.footer_tagline || ""} onChange={e => set("footer_tagline", e.target.value)} className={inputCls} placeholder="Commercial Printing • Nationwide Shipping" /></Field>
          <Field label="Copyright Text" help={`Leave blank to auto-generate "© ${new Date().getFullYear()} <Business Name>"`}><input value={data.copyright_text || ""} onChange={e => set("copyright_text", e.target.value)} className={inputCls} /></Field>
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">SEO & Analytics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="SEO Meta Title" help="Browser tab + Google search title"><input value={data.seo_title || ""} onChange={e => set("seo_title", e.target.value)} className={inputCls} /></Field>
          <Field label="SEO Meta Description" help="~150 characters for search snippets"><textarea rows={3} value={data.seo_description || ""} onChange={e => set("seo_description", e.target.value)} className={inputCls} /></Field>
          <Field label="Google Analytics ID" help="Format: G-XXXXXXXXXX"><input value={data.google_analytics_id || ""} onChange={e => set("google_analytics_id", e.target.value)} className={inputCls} placeholder="G-XXXXXXXXXX" /></Field>
          <Field label="Meta (Facebook) Pixel ID"><input value={data.meta_pixel_id || ""} onChange={e => set("meta_pixel_id", e.target.value)} className={inputCls} /></Field>
          <Field label="Google Search Console Verification" help="Paste the content= value from the verification meta tag"><input value={data.google_search_console_verification || ""} onChange={e => set("google_search_console_verification", e.target.value)} className={inputCls} /></Field>
        </div>
      </section>

      <div className="pt-8 border-t border-[#E5E5E5] flex items-center gap-4">
        <button onClick={save} disabled={saving} data-testid="settings-save"
          className="bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-10 py-4 text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-50">
          {saving ? "Saving..." : "Save Settings"}
        </button>
        <p className="text-xs text-[#888]">Changes apply immediately across the entire site.</p>
      </div>
    </div>
  );
}
